import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import LocalizedText from '@LocalizedText';
import LocalizedImage from '@LocalizedImage';
import { smoothScrolling } from '@app/utils/utils';
import {
  HELP_SECTION_ART_SIZE_POSITION,
  HELP_TOPIC_ART_SIZE_POSITION,
  HELP_TOPIC_ART_SIZE_POSITION_STB_LINES,
  HELP_TOPIC_ART_SIZE_POSITION_STRETCHED,
  HELP_TOPIC_ART_SIZE_POSITION_OPTIMIZED,
  HELP_TOPIC_ART_SIZE_POSITION_FLATTENED,
  HELP_TOPIC_ART_SIZE_POSITION_ADJUSTED,
  HELP_TOPIC_SHAPE_CORNERS_SIZE,
  HELP_ART_SIZE_LABELS,
} from '@app/constants';
import { setLabelTypePart } from '@app/utils/help';

const ArtSizePositionSection = ({ activeTopic, scrollToTopic, isKnownShapeSticker, isCustomShapeSticker, isSheetFlow }) => {
  const labelTypePart = setLabelTypePart(HELP_ART_SIZE_LABELS, [isCustomShapeSticker, isKnownShapeSticker, isSheetFlow]);

  useEffect(() => {
    activeTopic && scrollToTopic(activeTopic);
  }, [activeTopic, scrollToTopic]);

  useEffect(() => {
    const anchors = document.querySelectorAll(`a[href^="#"]`);
    anchors && anchors.forEach(anchor => anchor.addEventListener('click', smoothScrolling));
    return () => anchors && anchors.forEach(anchor => anchor.removeEventListener('click', smoothScrolling));
  }, []);

  return (
    <div id={HELP_SECTION_ART_SIZE_POSITION} className="help-section">
      <div id={HELP_TOPIC_ART_SIZE_POSITION} className="section-topic">
        <LocalizedText id={`qau-help-topic-${labelTypePart}`} tagName="h2" />
        <LocalizedText id={`qau-help-topic-${labelTypePart}-topic-desc-p1`} tagName="p" />
        <LocalizedText id={`qau-help-topic-${labelTypePart}-topic-desc-p2`} tagName="p" />
        {isCustomShapeSticker || isSheetFlow ? (
          <LocalizedText id={`qau-help-topic-${labelTypePart}-topic-desc-p3`} tagName="p" />
        ) : (
          <>
            <LocalizedText id={`qau-help-topic-${labelTypePart}-transform-art`} tagName="h3" />
            <div>
              <LocalizedText
                id={`qau-help-topic-${labelTypePart}-transform-art-p1`}
                values={{ stbLinesTopicKey: HELP_TOPIC_ART_SIZE_POSITION_STB_LINES }}
                tagName="p"
              />
              <LocalizedText id={`qau-help-topic-${labelTypePart}-transform-art-p2`} tagName="p" />
              <LocalizedText id={`qau-help-topic-${labelTypePart}-transform-art-p3`} tagName="p" />
              <LocalizedText id={`qau-help-topic-${labelTypePart}-transform-art-p4`} tagName="p" />
            </div>
          </>
        )}
      </div>
      {isSheetFlow && (
        <>
          <div className="section-topic">
            <LocalizedText id={`qau-help-topic-zoom-${labelTypePart}`} tagName="h3" />
            <div>
              <LocalizedText id={`qau-help-topic-zoom-${labelTypePart}-p1`} tagName="p" />
              <LocalizedText id={`qau-help-topic-zoom-${labelTypePart}-p2`} tagName="p" />
              <LocalizedText
                id={`qau-help-topic-zoom-${labelTypePart}-p3`}
                tagName="p"
                values={{ stbLinesTopicKey: HELP_TOPIC_ART_SIZE_POSITION_STB_LINES }}
              />
              <LocalizedText id={`qau-help-topic-zoom-${labelTypePart}-p4`} tagName="p" />
              <LocalizedText id={`qau-help-topic-zoom-${labelTypePart}-p5`} tagName="p" />
              <LocalizedText id={`qau-help-topic-zoom-${labelTypePart}-p6`} tagName="p" />
            </div>
          </div>
          <div className="section-topic" id={HELP_TOPIC_SHAPE_CORNERS_SIZE}>
            <LocalizedText id={`qau-help-topic-shape-corners-size-${labelTypePart}`} tagName="h3" />
            <div>
              <LocalizedText id={`qau-help-topic-shape-corners-size-${labelTypePart}-p1`} tagName="p" />
              <LocalizedText id={`qau-help-topic-shape-corners-size-${labelTypePart}-p2`} tagName="p" />
              <LocalizedText id={`qau-help-topic-shape-corners-size-${labelTypePart}-p3`} tagName="p" />
              <LocalizedText id={`qau-help-topic-shape-corners-size-${labelTypePart}-p4`} tagName="p" />
              <LocalizedText id={`qau-help-topic-shape-corners-size-${labelTypePart}-p5`} tagName="p" />
            </div>
          </div>
        </>
      )}
      {isKnownShapeSticker && (
        <div className="section-topic">
          <LocalizedText id={`qau-help-topic-${labelTypePart}-size-art`} tagName="h3" />
          <div>
            <LocalizedText id={`qau-help-topic-${labelTypePart}-size-art-p1`} tagName="p" />
            <LocalizedText id={`qau-help-topic-${labelTypePart}-size-art-p2`} tagName="p" />
          </div>
        </div>
      )}
      {!isCustomShapeSticker && !isSheetFlow && (
        <div className="section-topic">
          <LocalizedText id={`qau-help-topic-${labelTypePart}-move-art`} tagName="h3" />
          <div>
            <LocalizedText id={`qau-help-topic-${labelTypePart}-move-art-p1`} tagName="p" />
            <LocalizedText id={`qau-help-topic-${labelTypePart}-move-art-p2`} tagName="p" />
          </div>
        </div>
      )}
      {!isCustomShapeSticker && !isSheetFlow && (
        <div className="section-topic">
          <LocalizedText id={`qau-help-topic-${labelTypePart}-scale-art`} tagName="h3" />
          <div>
            <LocalizedText id={`qau-help-topic-${labelTypePart}-scale-art-p1`} tagName="p" />
            <LocalizedText id={`qau-help-topic-${labelTypePart}-scale-art-p2`} tagName="p" />
          </div>
        </div>
      )}
      {!isCustomShapeSticker && (
        <div id={ HELP_TOPIC_ART_SIZE_POSITION_STB_LINES } className="section-topic">
          <LocalizedText id={`qau-help-topic-${labelTypePart}-stb-lines`} tagName="h3" />
          <div>
            <LocalizedText id={`qau-help-topic-${labelTypePart}-stb-lines-p1`} tagName="p" />
            <LocalizedText id={`qau-help-topic-${labelTypePart}-stb-lines-p2`} tagName="p" />
            <LocalizedText id={`qau-help-topic-${labelTypePart}-stb-lines-p3`} tagName="p" />
            <LocalizedText id={`qau-help-topic-${labelTypePart}-stb-lines-p4`} tagName="p" />
            <div className="image-block image-art-position">
              <LocalizedImage name="art-position-stb-lines.png" alt="Safe, trim & bleed lines example" allowDefault={true} />
            </div>
            <LocalizedText id={`qau-help-topic-${labelTypePart}-stb-lines-note`} tagName="p" />
          </div>
        </div>
      )}
      {!isSheetFlow && (
        <div id={HELP_TOPIC_ART_SIZE_POSITION_STRETCHED} className="section-topic">
          <LocalizedText id={`qau-help-topic-${labelTypePart}-art-stretched`} tagName="h3" />
          <div>
            <LocalizedText id={`qau-help-topic-${labelTypePart}-art-stretched-p1`} tagName="p" />
            {isCustomShapeSticker && <LocalizedText id={`qau-help-topic-${labelTypePart}-art-stretched-p2`} tagName="p" />}
          </div>
        </div>
      )}
      <div id={HELP_TOPIC_ART_SIZE_POSITION_OPTIMIZED} className="section-topic">
        <LocalizedText id={`qau-help-topic-${labelTypePart}-art-optimized`} tagName="h3" />
        <div>
          <LocalizedText id={`qau-help-topic-${labelTypePart}-art-optimized-p1`} tagName="p" />
          <LocalizedText id={`qau-help-topic-${labelTypePart}-art-optimized-p2`} tagName="p" />
        </div>
      </div>
      <div id={HELP_TOPIC_ART_SIZE_POSITION_FLATTENED} className="section-topic">
        <LocalizedText id={`qau-help-topic-${labelTypePart}-art-flattened`} tagName="h3" />
        <div>
          <LocalizedText id={`qau-help-topic-${labelTypePart}-art-flattened-p1`} tagName="p" />
        </div>
      </div>
      <div id={HELP_TOPIC_ART_SIZE_POSITION_ADJUSTED} className="section-topic">
        <LocalizedText id={`qau-help-topic-${labelTypePart}-art-adjusted`} tagName="h3" />
        <div>
          <LocalizedText id={`qau-help-topic-${labelTypePart}-art-adjusted-p1`} tagName="p" />
          {isCustomShapeSticker && <LocalizedText id={`qau-help-topic-${labelTypePart}-art-adjusted-p2`} tagName="p" />}
        </div>
      </div>
    </div>
  );
};

ArtSizePositionSection.propTypes = {
  scrollToTopic: PropTypes.func.isRequired,
  activeTopic: PropTypes.string,
  isKnownShapeSticker: PropTypes.bool.isRequired,
  isCustomShapeSticker: PropTypes.bool.isRequired,
  isSheetFlow: PropTypes.bool.isRequired,
};

export default ArtSizePositionSection;
